import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import { SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import type {
  CreateRelationDto,
  RelationType,
  CriticalityLevel,
} from '../../../../shared/src/types/relation.types';
import {
  getValidRelationTypes,
  type ComponentType,
} from '../../../../shared/src/types/relation-mappings.types';

interface RelationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRelationDto) => Promise<void>;
  fromComponent?: {
    id: string;
    name: string;
    type: ComponentType;
  };
  toComponent?: {
    id: string;
    name: string;
    type: ComponentType;
  };
  mode?: 'create' | 'edit';
  initialData?: {
    id?: string;
    type?: RelationType;
    description?: string;
    criticality?: CriticalityLevel;
    protocol?: string;
    port?: number;
    isEncrypted?: boolean;
  };
  onSwapDirection?: () => void;
}

interface RelationFormData {
  type: RelationType;
  description: string;
  criticality: CriticalityLevel | '';
  protocol: string;
  port: number | '';
  isEncrypted: boolean;
}

export const RelationForm: React.FC<RelationFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  fromComponent,
  toComponent,
  mode = 'create',
  initialData,
  onSwapDirection,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { control, handleSubmit, watch, reset } = useForm<RelationFormData>({
    defaultValues: {
      type: '' as RelationType,
      description: '',
      criticality: '',
      protocol: '',
      port: '',
      isEncrypted: false,
    },
  });

  // Load initial data when opening in edit mode
  useEffect(() => {
    if (isOpen && initialData) {
      reset({
        type: initialData.type || ('' as RelationType),
        description: initialData.description || '',
        criticality: initialData.criticality || '',
        protocol: initialData.protocol || '',
        port: initialData.port || '',
        isEncrypted: initialData.isEncrypted || false,
      });
    } else if (isOpen && !initialData) {
      reset({
        type: '' as RelationType,
        description: '',
        criticality: '',
        protocol: '',
        port: '',
        isEncrypted: false,
      });
    }
  }, [isOpen, initialData, reset]);

  // Get valid relation types based on component types
  const validRelationTypes =
    fromComponent && toComponent
      ? getValidRelationTypes(fromComponent.type, toComponent.type)
      : [];

  const selectedType = watch('type');
  const showNetworkFields =
    selectedType === 'CONNECTS_TO' ||
    selectedType === 'CALLS_API' ||
    selectedType === 'EXPOSES_SERVICE';

  const handleFormSubmit = async (data: RelationFormData) => {
    if (!fromComponent || !toComponent) {
      setError(t('relation.error.selectComponents'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const relationData: CreateRelationDto = {
        fromEntityId: fromComponent.id,
        toEntityId: toComponent.id,
        type: data.type,
        description: data.description || undefined,
        criticality: (data.criticality as CriticalityLevel) || undefined,
        protocol: data.protocol || undefined,
        port: data.port ? Number(data.port) : undefined,
        isEncrypted: data.isEncrypted,
      };

      await onSubmit(relationData);
      onClose();
    } catch (err) {
      setError((err as Error).message || t('relation.error.create'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>
        {mode === 'edit' ? t('relation.edit') : t('relation.create')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Source and Target Components */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid size={5}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('relation.from')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {fromComponent?.name || t('relation.selectSource')}
                </Typography>
                {fromComponent && (
                  <Chip
                    label={t(`componentTypes:${fromComponent.type}`)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Grid>
            <Grid size={2} sx={{ textAlign: 'center' }}>
              {onSwapDirection ? (
                <Tooltip title={t('relation.swapDirection')}>
                  <IconButton
                    onClick={onSwapDirection}
                    color="primary"
                    size="large"
                  >
                    <SwapHorizIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Typography variant="h5" color="text.secondary">
                  →
                </Typography>
              )}
            </Grid>
            <Grid size={5}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('relation.to')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {toComponent?.name || t('relation.selectTarget')}
                </Typography>
                {toComponent && (
                  <Chip
                    label={t(`componentTypes:${toComponent.type}`)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Grid container spacing={2}>
            {/* Relation Type */}
            <Grid size={12}>
              <Controller
                name="type"
                control={control}
                rules={{ required: t('validation.required') }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={validRelationTypes}
                    value={
                      validRelationTypes.find(r => r.type === field.value) ||
                      null
                    }
                    onChange={(_event, newValue) => {
                      field.onChange(newValue?.type || '');
                    }}
                    getOptionLabel={option =>
                      t(`relation.types.${option.type}`)
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('relation.type')}
                        required
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box>
                            <Typography variant="body1">
                              {t(`relation.types.${option.type}`)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.type === value.type
                    }
                    autoHighlight
                    openOnFocus
                    noOptionsText={t('relation.noValidTypes')}
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid size={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('relation.description')}
                    multiline
                    rows={2}
                    placeholder={t('relation.descriptionPlaceholder')}
                  />
                )}
              />
            </Grid>

            {/* Criticality */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="criticality"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>{t('relation.criticality')}</InputLabel>
                    <Select {...field} label={t('relation.criticality')}>
                      <MenuItem value="">{t('common.none')}</MenuItem>
                      <MenuItem value="critical">
                        {t('relation.criticalityLevels.critical')}
                      </MenuItem>
                      <MenuItem value="high">
                        {t('relation.criticalityLevels.high')}
                      </MenuItem>
                      <MenuItem value="medium">
                        {t('relation.criticalityLevels.medium')}
                      </MenuItem>
                      <MenuItem value="low">
                        {t('relation.criticalityLevels.low')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Network Fields (conditional) */}
            {showNetworkFields && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="protocol"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('relation.protocol')}
                        placeholder="HTTPS, TCP, gRPC"
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="port"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('relation.port')}
                        type="number"
                        inputProps={{ min: 1, max: 65535 }}
                        placeholder="443"
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="isEncrypted"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label={t('relation.isEncrypted')}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          variant="contained"
          disabled={isSubmitting || !fromComponent || !toComponent}
        >
          {isSubmitting ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
